/**
 * Institution Types for BigQuery API
 *
 * Types for the dynamic institution data from BigQuery.
 * These extend the base types for the new API endpoints.
 */

import type { ComponentScores, RatingCategory } from '../types';

// ============================================================================
// RATED INSTITUTION TYPES (from /api/institutions/rated)
// ============================================================================

/**
 * Rated institution with location and segment metadata
 * Response from GET /api/institutions/rated
 */
export interface RatedInstitution {
  institution_id: string;
  institution_name: string;
  province: string;
  city: string;
  country: string;
  segment_id: string;
  segment_name: string;
}

/**
 * Rated institution with latest rating data
 * Response from GET /api/institutions/rated-with-ratings
 */
export interface RatedInstitutionWithRating extends RatedInstitution {
  rating_category: RatingCategory;
  numeric_score: number;
  component_scores: ComponentScores;
  period: string; // YYYY-MM format
}

// ============================================================================
// RATING TYPES (from /api/institutions/:id/ratings)
// ============================================================================

/**
 * Institution rating summary
 * Response from GET /api/institutions/:id/ratings/latest
 */
export interface InstitutionRating {
  institution_id: string;
  rating_category: RatingCategory;
  numeric_score: number;
  component_scores: ComponentScores;
  period: string; // YYYY-MM format
}

/**
 * Rating history entry (same shape as InstitutionRating)
 */
export type RatingHistoryEntry = InstitutionRating;

// ============================================================================
// INDICATOR TYPES (from /api/institutions/:id/indicators)
// ============================================================================

/**
 * Indicator score for an institution/period
 * Response from GET /api/institutions/:id/indicators
 */
export interface IndicatorScore {
  indicator_id: string;
  indicator_name: string;
  indicator_category: string;
  indicator_value: number;
  indicator_score: number;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Response from GET /api/institutions/rated
 */
export interface RatedInstitutionsResponse {
  success: boolean;
  count: number;
  institutions: RatedInstitution[];
}

/**
 * Response from GET /api/institutions/rated-with-ratings
 */
export interface RatedInstitutionsWithRatingsResponse {
  success: boolean;
  count: number;
  institutions: RatedInstitutionWithRating[];
}

/**
 * Response from GET /api/institutions/:id/ratings/latest
 */
export interface LatestRatingResponse {
  success: boolean;
  rating: InstitutionRating | null;
}

/**
 * Response from GET /api/institutions/:id/ratings
 */
export interface RatingHistoryResponse {
  success: boolean;
  count: number;
  ratings: RatingHistoryEntry[];
}

/**
 * Response from GET /api/institutions/:id/indicators
 */
export interface IndicatorScoresResponse {
  success: boolean;
  count: number;
  period: string;
  indicators: IndicatorScore[];
}

/**
 * Response from GET /api/institutions/cache/stats
 */
export interface CacheStatsResponse {
  success: boolean;
  stats: {
    institutions: number;
    ratings: number;
    indicators: number;
    ttlHours: number;
  };
}

/**
 * Response from POST /api/institutions/cache/clear
 */
export interface CacheClearResponse {
  success: boolean;
  message: string;
  cleared: {
    institutions: number;
    ratings: number;
    indicators: number;
  };
}

// ============================================================================
// TRANSFORMATION TYPES
// For compatibility with existing frontend data formats
// ============================================================================

/**
 * CAMEL score array format (used by existing components)
 */
export interface CAMELScoreArray {
  code: string;
  score: number;
}

/**
 * Transform component_scores object to CAMEL array format
 */
export function componentScoresToArray(scores: ComponentScores): CAMELScoreArray[] {
  return [
    { code: 'C', score: scores.C },
    { code: 'EG', score: scores.EG },
    { code: 'L', score: scores.L },
    { code: 'R', score: scores.R },
    { code: 'S', score: scores.S },
    { code: 'STI', score: scores.STI },
  ];
}

/**
 * Transform CAMEL array to component_scores object
 */
export function arrayToComponentScores(array: CAMELScoreArray[]): ComponentScores {
  const scores: ComponentScores = { C: 0, EG: 0, L: 0, R: 0, S: 0, STI: 0 };
  for (const item of array) {
    if (item.code in scores) {
      scores[item.code as keyof ComponentScores] = item.score;
    }
  }
  return scores;
}

// ============================================================================
// PROVINCE AGGREGATION TYPES (for RatingsMap)
// ============================================================================

/**
 * Province statistics for map visualization
 */
export interface ProvinceStats {
  province: string;
  institutionCount: number;
  avgScore: number;
  institutions: RatedInstitutionWithRating[];
}

/**
 * Aggregate institutions by province
 */
export function aggregateByProvince(
  institutions: RatedInstitutionWithRating[]
): Map<string, ProvinceStats> {
  const provinceMap = new Map<string, ProvinceStats>();

  for (const inst of institutions) {
    const existing = provinceMap.get(inst.province);
    if (existing) {
      existing.institutions.push(inst);
      existing.institutionCount = existing.institutions.length;
      existing.avgScore =
        existing.institutions.reduce((sum, i) => sum + i.numeric_score, 0) /
        existing.institutions.length;
    } else {
      provinceMap.set(inst.province, {
        province: inst.province,
        institutionCount: 1,
        avgScore: inst.numeric_score,
        institutions: [inst],
      });
    }
  }

  return provinceMap;
}
